<script setup lang="ts">
import { computed, reactive } from 'vue'
import type { PropType } from 'vue'

interface ReasonItem {
    title: string
    desc: string
    url: string
    thumbnail_url?: string
    is_html_desc?: boolean
}

const props = defineProps({
    title: {
        type: String,
        default: 'REASON',
    },
    btnInfo: {
        type: String,
        default: '',
    },
    desc: {
        type: Array as PropType<ReasonItem[]>,
        default: () => [],
    },
    customCategory: {
        type: String,
        default: '',
    },
    keyFeatureTitle: {
        type: String,
        default: '',
    },
    keyFeatureDesc: {
        type: String,
        default: '',
    },
    img_class: {
        type: String,
        default: '',
    },
})

// 视频加载状态映射
const videoLoadedMap = reactive<Record<number, boolean>>({})

// 视频加载完成回调
const handleVideoLoaded = (index: number) => {
    videoLoadedMap[index] = true
}

const computedDesc = computed(() =>
    (props.desc || []).map((item, index) => {
        const url = item.url || ''
        const thumbnailUrl = item.thumbnail_url || ''
        const hasMedia = !!url
        const isVideo = hasMedia ? /\.(mp4|webm|ogg)(\?.*)?$/i.test(url) : false

        return {
            ...item,
            url,
            thumbnailUrl,
            hasMedia,
            isVideo,
            index,
        }
    })
)

// 点击按钮，回到顶部
const returnTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth',
    })
}
</script>

<template>
    <div class="selectReason">
        <div
            class="title"
            :class="[!keyFeatureTitle ? '!mb-[60px]' : '!mb-[0]']"
        >
            {{ keyFeatureTitle || title }}
        </div>
        <h2
            v-if="keyFeatureDesc"
            class="text-base max-w-4xl mx-auto mb-[60px] text-text-adad text-center"
        >
            {{ keyFeatureDesc }}
        </h2>
        <div class="waterfall">
            <div
                v-for="(item, index) in computedDesc"
                :key="index"
                class="desc-container"
                :class="index % 2 === 0 ? 'reverse' : ''"
            >
                <div class="desc-content">
                    <div class="content-title">{{ item.title }}</div>
                    <div
                        v-if="item?.is_html_desc"
                        v-html="item.desc"
                        class="content-desc"
                    ></div>
                    <div v-else class="content-desc">{{ item.desc }}</div>
                    <div
                        class="try-tools"
                        v-if="customCategory == 'effects'"
                        @click="returnTop"
                    >
                        {{ btnInfo }}
                    </div>
                </div>
                <div v-if="item.hasMedia" class="media">
                    <!-- 封面图片：视频未加载时显示 -->
                    <img
                        v-if="item.isVideo && item.thumbnailUrl && !videoLoadedMap[item.index]"
                        :src="item.thumbnailUrl"
                        :alt="item.title || 'Video upscaler feature thumbnail'"
                        class="media-cover"
                        loading="lazy"
                    />
                    <!-- 视频：加载完成后显示 -->
                    <video
                        v-if="item.isVideo"
                        v-show="!item.thumbnailUrl || videoLoadedMap[item.index]"
                        :src="item.url"
                        autoplay
                        loop
                        muted
                        crossorigin="anonymous"
                        playsinline
                        webkit-playsinline
                        @canplay="handleVideoLoaded(item.index)"
                    ></video>
                    <!-- 图片：直接显示 -->
                    <img v-else-if="!item.isVideo" :src="item.url" :alt="item.title || 'Video upscaler feature image'" loading="lazy" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="less">
.selectReason {
    max-width: 1280px;
    margin: 0 auto;

    .title {
        font-size: 3.75rem;
        font-weight: 700;
        text-align: center;
        margin-bottom: 60px;
        color: #FFFFFF;
    }

    .waterfall {
        border-radius: 20px;
        overflow: hidden;

        .desc-container {
            display: flex;
            column-gap: 30px;
            align-items: center;
            box-sizing: border-box;
            padding: 50px;
            justify-content: space-around;
            min-height: 400px;

            &.reverse {
                flex-direction: row-reverse;
            }

            @media (max-width: 768px) {
                flex-direction: column !important;
                padding: 30px 20px;
                min-height: auto;

                .desc-content {
                    margin-bottom: 20px;
                }

                .media {
                    border-radius: 20px;
                    width: 100%;
                }
            }

            @media (max-width: 480px) {
                padding: 20px 16px;
            }
        }

        .desc-container:first-of-type {
            background: linear-gradient(
                180deg,
                #341012,
                #220f11,
                #190f11,
                #100e11
            );
        }

        .desc-container:last-of-type {
            background: linear-gradient(
                0deg,
                #341012,
                #220f11,
                #190f11,
                #100e11
            );
        }
    }

    @media (max-width: 768px) {
        .title {
            font-size: 2rem;
            margin-bottom: 40px;
            padding: 0 16px;
        }
    }

    @media (max-width: 480px) {
        .title {
            font-size: 1.5rem;
            margin-bottom: 30px;
        }
    }
}

.waterfall {
    .desc-content {
        font-weight: 500;
        text-align: left;
        flex: 0 0 35%;
        max-width: 35%;

        .content-title {
            font-size: 2.25rem;
            font-weight: 500;
            color: #FFFFFF;
        }

        .content-desc {
            font-size: 1rem;
            font-weight: 500;
            color: #adadad;
        }
    }

    .media {
        width: 25%;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
    }

    .media img {
        width: 100%;
        // height: auto;
        border-radius: 20px;
    }

    .media img.media-cover {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .media video {
        width: 100%;
        max-width: 100%;
        height: auto;
        max-height: 400px;
        border-radius: 20px;
        object-fit: contain;
    }

    .try-tools {
        border: 1px solid #98f668;
        padding: 5px 0;
        margin-top: 10px;
        text-align: center;
        border-radius: 10px;
        cursor: pointer;
        color: #FFFFFF;
    }

    @media (max-width: 768px) {
        .desc-content {
            flex: 1;
            max-width: 100%;
            text-align: center;

            .content-title {
                font-size: 1.5rem;
                margin-bottom: 12px;
            }

            .content-desc {
                font-size: 0.875rem;
            }
        }

        .media {
            width: 100%;

            video {
                max-height: 300px;
                border-radius: 12px;
            }

            img {
                border-radius: 12px;
            }
        }

        .try-tools {
            padding: 8px 0;
            font-size: 0.875rem;
        }
    }

    @media (max-width: 480px) {
        .desc-content {
            .content-title {
                font-size: 1.25rem;
            }

            .content-desc {
                font-size: 0.8rem;
            }
        }

        .media {
            video {
                max-height: 200px;
            }
        }
    }
}
</style>
